// File: frontend/src/components/ems/EventTable.js

import React from 'react';
import { Eye, ChevronRight, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import EmptyState from '../utility/EmptyState';
import styles from './EventTable.module.css';

const EventTable = ({ 
  events, 
  totalCount, 
  isLoading, 
  hasMore, 
  onLoadMore, 
  onViewDetails,
  onViewUser
}) => {
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ');
  };

  const getEventTypeBadgeClass = (eventType) => {
    if (eventType.includes('SIGN_IN') || eventType.includes('SIGN_UP')) {
      return styles.badgeSuccess;
    }
    if (eventType.includes('ERROR') || eventType.includes('FAIL')) {
      return styles.badgeError;
    }
    if (eventType.includes('EMAIL') || eventType.includes('NOTIFICATION')) {
      return styles.badgeInfo;
    }
    return styles.badgeDefault;
  };

  if (isLoading && events.length === 0) {
    return (
      <div className={styles.tableCard}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="large" text="Loading events..." />
        </div>
      </div>
    );
  }

  if (!isLoading && events.length === 0) {
    return (
      <div className={styles.tableCard}>
        <EmptyState
          title="No events found"
          message="Try adjusting your filters or search criteria"
        />
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      {/* Header */}
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>
          Events 
          {totalCount !== null && (
            <span className={styles.eventCount}>
              ({events.length} of {totalCount})
            </span>
          )}
          {totalCount === null && (
            <span className={styles.eventCount}>
              ({events.length})
            </span>
          )}
        </h2>
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileView}>
        {events.map((event) => (
          <div key={event.event_id} className={styles.eventCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <span className={`${styles.badge} ${getEventTypeBadgeClass(event.event_type)}`}>
                  {formatEventType(event.event_type)}
                </span>
                <div className={styles.eventTime}>{formatDate(event.event_timestamp)}</div>
              </div>
              <button
                onClick={() => onViewDetails(event)}
                className={styles.viewButtonMobile}
                aria-label="View event details"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Aggregate ID:</span>
                <span className={styles.cardValue}>{event.aggregate_id}</span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Created By:</span>
                <span className={styles.cardValue}>{event.created_by}</span>
              </div>

              {event.user_id && (
                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>User ID:</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewUser(event.user_id);
                    }}
                    className={styles.userLinkButton}
                  >
                    {event.user_id}
                    <ExternalLink size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className={styles.desktopView}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Event Type</th>
                <th>Aggregate ID</th>
                <th>Created By</th>
                <th>User ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.event_id}>
                  <td className={styles.timestampCell}>
                    {formatDate(event.event_timestamp)}
                  </td>

                  <td>
                    <span className={`${styles.badge} ${getEventTypeBadgeClass(event.event_type)}`}>
                      {formatEventType(event.event_type)}
                    </span>
                  </td>

                  <td className={styles.aggregateCell}>
                    {event.aggregate_id}
                  </td>

                  <td className={styles.createdByCell}>
                    {event.created_by}
                  </td>

                  <td>
                    {event.user_id ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewUser(event.user_id);
                        }}
                        className={styles.userLinkButton}
                      >
                        {event.user_id}
                        <ExternalLink size={12} />
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>

                  <td>
                    <button
                      onClick={() => onViewDetails(event)}
                      className={styles.viewButton}
                      aria-label="View event details"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className={styles.loadMoreContainer}>
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className={styles.loadMoreButton}
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="small" />
                Loading...
              </>
            ) : (
              'Load More Events'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default EventTable;